/**
 * Limited Edition System for Shopify
 * Handles edition tracking, cart updates, and sold-out states
 */

class LimitedEditionSystem {
  constructor() {
    this.products = new Map();
    this.init();
  }

  init() {
    // Initialize all limited edition products on the page
    this.initializeProducts();
    
    // Listen for cart updates
    this.setupCartListeners();
    
    // Listen for variant changes
    this.setupVariantListeners();
  }

  initializeProducts() {
    const editionElements = document.querySelectorAll('.limited-edition-info');
    
    editionElements.forEach(element => {
      const productId = element.dataset.productId;
      const maxEditions = parseInt(element.dataset.maxEditions);
      const soldEditions = parseInt(element.dataset.soldEditions);
      
      this.products.set(productId, {
        element: element,
        maxEditions: maxEditions,
        soldEditions: soldEditions,
        remainingEditions: maxEditions - soldEditions
      });
      
      this.updateProductDisplay(productId);
    });
  }

  setupCartListeners() {
    // Listen for cart updates from Shopify's cart system
    document.addEventListener('cart:updated', (event) => {
      this.handleCartUpdate(event.detail);
    });
    
    // Listen for custom cart events
    document.addEventListener('cart:item-added', (event) => {
      this.handleItemAdded(event.detail);
    });
  }

  setupVariantListeners() {
    // Listen for variant changes on product forms
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name*="option"], select[name*="option"]')) {
        const productForm = event.target.closest('form[data-type="add-to-cart-form"]');
        if (productForm) {
          this.handleVariantChange(productForm);
        }
      }
    });
  }

  handleCartUpdate(cartData) {
    // Update edition counts when cart is updated
    if (cartData && cartData.items) {
      cartData.items.forEach(item => {
        this.updateEditionCount(item.product_id, item.quantity);
      });
    }
  }

  handleItemAdded(itemData) {
    // Update edition count when item is added to cart
    if (itemData && itemData.product_id) {
      this.updateEditionCount(itemData.product_id, itemData.quantity || 1);
    }
  }

  handleVariantChange(productForm) {
    const productId = productForm.dataset.productId;
    if (productId && this.products.has(productId)) {
      this.updateProductDisplay(productId);
    }
  }

  updateEditionCount(productId, quantity) {
    const product = this.products.get(productId);
    if (!product) return;

    // Update sold editions count
    product.soldEditions += quantity;
    product.remainingEditions = product.maxEditions - product.soldEditions;
    
    // Update the data attributes
    product.element.dataset.soldEditions = product.soldEditions;
    
    // Update display
    this.updateProductDisplay(productId);
    
    // Check if product is now sold out
    if (product.remainingEditions <= 0) {
      this.markProductAsSoldOut(productId);
    }
  }

  updateProductDisplay(productId) {
    const product = this.products.get(productId);
    if (!product) return;

    const currentEdition = product.soldEditions + 1;
    const remainingEditions = product.remainingEditions;
    
    // Update edition counter
    const currentElement = product.element.querySelector('.edition-current');
    if (currentElement) {
      currentElement.textContent = currentEdition;
    }
    
    // Update progress bar
    const progressFill = product.element.querySelector('.progress-fill');
    if (progressFill) {
      const percentage = (product.soldEditions / product.maxEditions) * 100;
      progressFill.style.width = `${percentage}%`;
    }
    
    // Update remaining count
    const remainingElement = product.element.querySelector('.remaining-count');
    if (remainingElement) {
      remainingElement.textContent = remainingEditions;
    }
    
    // Update remaining label
    const remainingLabel = product.element.querySelector('.remaining-label');
    if (remainingLabel) {
      const labelKey = remainingEditions === 1 ? 'remaining_singular' : 'remaining_plural';
      remainingLabel.textContent = this.getTranslation(labelKey);
    }
  }

  markProductAsSoldOut(productId) {
    const product = this.products.get(productId);
    if (!product) return;

    // Add sold-out class
    product.element.classList.add('sold-out');
    
    // Show sold-out badge
    const soldOutBadge = product.element.querySelector('.sold-out-badge');
    if (soldOutBadge) {
      soldOutBadge.style.display = 'inline-block';
    }
    
    // Disable add to cart button
    this.disableAddToCart(productId);
    
    // Update product availability
    this.updateProductAvailability(productId, false);
  }

  disableAddToCart(productId) {
    const productForm = document.querySelector(`form[data-product-id="${productId}"]`);
    if (!productForm) return;

    const submitButton = productForm.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('disabled');
      
      // Update button text
      const buttonText = submitButton.querySelector('span');
      if (buttonText) {
        buttonText.textContent = this.getTranslation('sold_out');
      }
    }
  }

  updateProductAvailability(productId, isAvailable) {
    // Update variant availability
    const variants = document.querySelectorAll(`input[name="id"][data-product-id="${productId}"]`);
    variants.forEach(variant => {
      variant.disabled = !isAvailable;
    });
    
    // Update variant options
    const optionInputs = document.querySelectorAll(`input[name*="option"][form*="${productId}"]`);
    optionInputs.forEach(input => {
      input.disabled = !isAvailable;
      if (!isAvailable) {
        input.classList.add('disabled');
      } else {
        input.classList.remove('disabled');
      }
    });
  }

  getTranslation(key) {
    const translations = {
      'remaining_singular': 'remaining',
      'remaining_plural': 'remaining',
      'sold_out': 'Sold Out'
    };
    
    return translations[key] || key;
  }

  // Public method to manually update edition count (for admin use)
  setEditionCount(productId, soldEditions) {
    const product = this.products.get(productId);
    if (!product) return;

    product.soldEditions = soldEditions;
    product.remainingEditions = product.maxEditions - soldEditions;
    
    product.element.dataset.soldEditions = soldEditions;
    
    this.updateProductDisplay(productId);
    
    if (product.remainingEditions <= 0) {
      this.markProductAsSoldOut(productId);
    } else {
      // Re-enable if previously sold out
      product.element.classList.remove('sold-out');
      this.enableAddToCart(productId);
    }
  }

  enableAddToCart(productId) {
    const productForm = document.querySelector(`form[data-product-id="${productId}"]`);
    if (!productForm) return;

    const submitButton = productForm.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-disabled');
      submitButton.classList.remove('disabled');
      
      // Restore button text
      const buttonText = submitButton.querySelector('span');
      if (buttonText) {
        buttonText.textContent = this.getTranslation('add_to_cart');
      }
    }
    
    this.updateProductAvailability(productId, true);
  }
}

// Initialize the system when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.limitedEditionSystem = new LimitedEditionSystem();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = LimitedEditionSystem;
}
